import { customFormat, parseCalendarDate } from 'utils/dateUtils'

import {
  AUTOMATED_LEVEL_BASED_TRAINING_IMAGE_PATH,
  CUSTOM_MODULE_IMAGE_PREVIEW,
  getProviderTag,
  TRAINING_VIDEO_TYPES,
} from 'containers/AwarenessCenter/WizardCampaign/steps/TrainingModuleStep/constants'

import { formatDurationClock } from 'components/pages/awareness-center/WizardCampaign/TrainingModuleStep/components/playlistUtils'

import { toCalendarIso } from '../collectionPeriodBounds'

const INFO_DATE_FORMAT = 'd MMM yyyy'

const EMPTY_MODULE = {
  title: null,
  vendor: null,
  isAltb: false,
  training: null,
  canPreview: false,
  thumbnail: null,
  duration: null,
  selectedLocaleIds: [],
}

const SETTINGS_ROWS = [
  { key: 'businessDaysOnly', text: 'Send emails during business days only' },
  { key: 'randomized', text: 'Send emails randomly between 9AM - 6PM' },
  { key: 'failureEmail', text: 'Notify recipients who fail with a failure email' },
  {
    key: 'sendSummaryToRecipientList',
    text: 'Send the campaign summary to the recipient list on campaign end',
    linkLabel: 'recipient list',
  },
]

export const EMPTY_CAMPAIGN_INFO = {
  available: false,
  id: null,
  name: null,
  state: null,
  extendedCount: 0,
  deliveryDate: null,
  collectingUntil: null,
  collectingUntilIso: null,
  fields: [],
  settings: [],
  module: EMPTY_MODULE,
  trainingPlaylist: null,
  message: {
    subject: null,
    fromEmail: null,
    fromName: null,
    displayName: null,
    initials: '?',
  },
}

export const formatCampaignInfoDate = (isoDate) => {
  const date = parseCalendarDate(isoDate)
  if (!date) {
    return null
  }

  return customFormat(date, INFO_DATE_FORMAT)
}

const toText = (value) => {
  if (value == null || value === '') {
    return null
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value)
  }
  return null
}

const formatCadence = (everyXDays, total) => {
  if (everyXDays == null) {
    return null
  }

  const cadence = `Every ${everyXDays} day${everyXDays === 1 ? '' : 's'}`
  return total == null ? cadence : `${cadence}, ${total} total`
}

const formatReminders = (reminders) =>
  toText(reminders) || formatCadence(reminders?.everyXDays, reminders?.total)

const formatManagerEscalation = (escalation) =>
  toText(escalation) || formatCadence(escalation?.everyXDays, escalation?.remindersThreshold)

const field = (icon, label, value) => (value ? { icon, label, value } : null)

const formatModuleDuration = (seconds) => {
  if (!seconds) {
    return null
  }

  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainder = seconds % 60
  const formattedSeconds = remainder.toString().length === 1 ? `0${remainder}` : `${remainder}`
  const formattedHours = hours > 0 ? `${hours}:` : ''
  const formattedMinutes =
    hours > 0 ? `${String(minutes).padStart(2, '0')}:` : `${minutes > 0 ? minutes : '00'}:`

  return `${formattedHours}${formattedMinutes}${formattedSeconds}`
}

const adaptTrainingItem = (item, type) => {
  const imagePreview =
    type === TRAINING_VIDEO_TYPES.CUSTOM_CONTENT.value
      ? CUSTOM_MODULE_IMAGE_PREVIEW
      : item.imagePreview || ''

  return {
    ...item,
    localeId: item.locale,
    customType: item.customType,
    imagePreview,
    content: item.content,
    duration: item.duration,
    embeddedSubtitles: item.embeddedSubtitles,
  }
}

const adaptTraining = (training, title) => {
  if (!training) {
    return null
  }

  const items = (training.items || []).map((item) => adaptTrainingItem(item, training.type))

  return {
    ...training,
    name: training.name || title || null,
    isDeleted: Boolean(training.isDeleted),
    wizerCourseId: training.wizerCourseId ?? training.wizer_course_id ?? null,
    items,
    subtitles: training.subtitles || [],
  }
}

const adaptModule = (module) => {
  if (!module) {
    return EMPTY_MODULE
  }

  const isAltb = Boolean(module.isAltb)
  const title = toText(module.title)
  const training = isAltb ? null : adaptTraining(module.training, title)
  const firstItem = training?.items?.[0]
  const selectedLocaleIds = (training?.items || [])
    .map((item) => item.localeId)
    .filter((localeId) => localeId != null)

  return {
    title,
    vendor: toText(module.vendor),
    isAltb,
    training,
    canPreview: !isAltb && Boolean(training) && !training.isDeleted,
    thumbnail: isAltb ? AUTOMATED_LEVEL_BASED_TRAINING_IMAGE_PATH : firstItem?.imagePreview || null,
    duration: isAltb ? null : formatModuleDuration(firstItem?.duration),
    selectedLocaleIds,
  }
}

// Entries are shaped like wizard videos so the S4 playlist components render them unchanged;
// each slot plays in its own BE-resolved localeId.
const adaptPlaylistVideo = (entry, index) => {
  // A live slot with no playable locale is as unavailable as a deleted one.
  const isDeleted = Boolean(entry.isDeleted) || entry.localeId == null

  return {
    id: entry.trainingId ?? `playlist-slot-${index}`,
    name: toText(entry.title),
    vendor: entry.vendorKey ?? null,
    is_deleted: isDeleted,
    items: isDeleted
      ? []
      : [
          {
            localeId: entry.localeId,
            duration: entry.duration,
            imagePreview: entry.thumbnail || '',
          },
        ],
  }
}

const adaptTrainingPlaylist = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return null
  }

  const videos = entries.map(adaptPlaylistVideo)
  const live = videos.filter((video) => !video.is_deleted)
  const totalSeconds = live.reduce(
    (total, video) => total + (Number(video.items[0].duration) || 0),
    0
  )
  const providers = new Map()
  live.forEach(({ vendor }) => {
    const tag = getProviderTag(vendor)
    if (tag && !providers.has(tag.label)) providers.set(tag.label, tag)
  })

  return {
    videos,
    count: live.length,
    totalDuration: formatDurationClock(totalSeconds),
    providers: [...providers.values()],
    thumbnail: live[0]?.items[0].imagePreview || null,
  }
}

const senderInitials = (fromName, fromEmail) => {
  if (fromName) {
    const parts = String(fromName).split(/\s+/).filter(Boolean).slice(0, 2)
    if (parts.length) {
      return parts.map((part) => part[0].toUpperCase()).join('')
    }
  }

  const local = String(fromEmail || '').split('@')[0]
  if (!local) {
    return '?'
  }

  return local.slice(0, 2).toUpperCase()
}

export const adaptCampaignInfo = (payload) => {
  if (payload == null || payload.available === false) {
    return EMPTY_CAMPAIGN_INFO
  }

  const fromName = payload.message?.fromName || null
  const fromEmail = payload.message?.fromEmail || null
  const module = adaptModule(payload.module)

  return {
    available: true,
    id: payload.id ?? null,
    name: payload.name || null,
    state: payload.state || null,
    extendedCount: payload.extendedCount || 0,
    deliveryDate: formatCampaignInfoDate(payload.deliveryDate),
    collectingUntil: formatCampaignInfoDate(payload.collectingUntil),
    collectingUntilIso: toCalendarIso(payload.collectingUntil),
    fields: [
      field('Calendar', 'Delivery date', formatCampaignInfoDate(payload.deliveryDate)),
      field('Flag', 'Collecting until', formatCampaignInfoDate(payload.collectingUntil)),
      field('Clock', 'Delivery time', toText(payload.deliveryTime)),
      field('Profile', 'Launched by', toText(payload.launchedBy)),
      field('Notification', 'Reminders', formatReminders(payload.reminders)),
      field(
        'Profile2User',
        'Manager escalation',
        formatManagerEscalation(payload.managerEscalation)
      ),
      field('Global', 'Timezone', toText(payload.timezone)),
    ].filter(Boolean),
    settings: SETTINGS_ROWS.map((row) => ({
      on: Boolean(payload.settings?.[row.key]),
      text: row.text,
      ...(row.linkLabel ? { linkLabel: row.linkLabel } : {}),
    })),
    module,
    trainingPlaylist: adaptTrainingPlaylist(payload.trainingPlaylist),
    message: {
      subject: toText(payload.message?.subject),
      fromEmail: toText(fromEmail),
      fromName,
      displayName: toText(fromName || fromEmail),
      initials: senderInitials(fromName, fromEmail),
    },
  }
}
